import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { formatPhoneDisplay } from '@/utils/phone'
import { Button } from '@/components/ui/Button'
import { LeadCallResultControl } from '@/components/inspections/LeadCallResultControl'
import { SendSmsToLeadButton } from '@/components/inspections/SendSmsToLeadButton'
import { SendStatusSmsButton } from '@/components/inspections/SendStatusSmsButton'
import { SendReportSmsButton } from '@/components/inspections/SendReportSmsButton'
import { LiveAgo } from '@/components/inspections/LiveAgo'
import { leadCardCls } from '@/components/inspections/leadDetail'
import { leadPipelineUi, type InspectionLeadRow } from '@/types/inspection-lead'

/**
 * Палитра действий лида (правая колонка, на мобильном — первой). Всё, что
 * меняет состояние лида, собрано здесь: взять/отпустить, этап, связь с
 * клиентом, «глазами клиента», Автотека, заказ, отмена. Что показывать —
 * по LEAD_PIPELINE_UI воронки.
 */
export function LeadWorkPalette({
  lead,
  isAssignedToMe,
  onClaim,
  onRelease,
  onCancel,
  claimPending,
  releasePending,
  cancelPending,
  error,
  flushDraft,
  stage,
  autoteka,
  order,
  manageExtra,
}: {
  lead: InspectionLeadRow
  isAssignedToMe: boolean
  onClaim: () => void
  onRelease: () => void
  onCancel: () => void
  claimPending: boolean
  releasePending: boolean
  cancelPending: boolean
  error: string | null
  /** Сохранить грязный draft (если есть). Вызывается перед SMS / открытием
   *  формы / статуса, чтобы оператор не потерял только что введённые поля. */
  flushDraft: () => Promise<void>
  /** Блоки «Этап» / «Автотека» / «Заказ» собирает страница; null — блок скрыт. */
  stage: ReactNode
  autoteka: ReactNode
  order: ReactNode
  /** Доп. управление в конце палитры (переключатель «Тестовый лид», только админ). */
  manageExtra?: ReactNode
}) {
  const { t } = useTranslation()
  const ui = leadPipelineUi(lead.pipeline)
  const terminal = ui.terminal.includes(lead.status)
  const hasToken = !!lead.landing_token && ui.landing
  // formUrl/statusUrl с флагом ?from=avs — диспетчер открывает «глазами
  // клиента», beacon на лендинге это видит и НЕ пишет form_opened /
  // status_page_viewed в lead_events (иначе ленту засоряли бы открытия
  // самих операторов). Кнопка «Скопировать ссылку» использует чистый
  // URL — она для отправки клиенту, должна работать как настоящая
  // клиентская ссылка из SMS.
  const formUrl = hasToken ? `https://avs.carlink.ru/z/${lead.landing_token}?from=avs` : null
  const statusUrl = hasToken ? `https://avs.carlink.ru/s/${lead.landing_token}?from=avs` : null
  const formUrlClean = hasToken ? `https://avs.carlink.ru/z/${lead.landing_token}` : null
  const statusUrlClean = hasToken ? `https://avs.carlink.ru/s/${lead.landing_token}` : null
  const formattedPhone = lead.client_phone ? formatPhoneDisplay(lead.client_phone) : null
  // Пока лид никем не взят в работу (assigned_to == null) — действия с клиентом
  // заблокированы: сначала «Взять в работу», потом звонок / SMS / просмотр ссылок.
  const notInWork = !lead.assigned_to

  const groupHead = 'mb-2 text-[10px] font-semibold uppercase tracking-wider text-[color:var(--stryzh-color-text-secondary)]'

  return (
    <section className={leadCardCls}>
      <div className="flex flex-col gap-4">
        {/* --- Работа с лидом --- */}
        <div>
          <div className={groupHead}>{t('leads.detail.palWork')}</div>
          {!lead.assigned_to ? (
            <Button
              type="button"
              variant="control"
              className="w-full"
              disabled={claimPending || terminal}
              onClick={onClaim}
            >
              {claimPending ? t('leads.detail.claiming') : t('leads.detail.claim')}
            </Button>
          ) : isAssignedToMe ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm text-[color:var(--stryzh-color-text-primary)]">
                {t('leads.detail.assignedToMe')}
                {lead.assigned_at ? (
                  <span className="ml-1 text-[color:var(--stryzh-color-text-secondary)]">
                    (<LiveAgo from={lead.assigned_at} />)
                  </span>
                ) : null}
              </span>
              {!terminal ? (
                <Button
                  type="button"
                  variant="control"
                  disabled={releasePending}
                  onClick={onRelease}
                >
                  {releasePending ? t('leads.detail.releasing') : t('leads.detail.release')}
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="text-sm text-[color:var(--stryzh-color-text-primary)]">
              {t('leads.detail.withPrefix')} <strong>{lead.assigned_to_name ?? '—'}</strong>
              {lead.assigned_at ? (
                <span className="ml-1 text-[color:var(--stryzh-color-text-secondary)]">
                  · <LiveAgo from={lead.assigned_at} />
                </span>
              ) : null}
            </div>
          )}
        </div>

        {/* --- Связь с клиентом --- */}
        <div>
          <div className={groupHead}>{t('leads.detail.palContact')}</div>
          {lead.awaiting_payment_sms_sent_at ? (
            <div className="mb-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {t('leads.detail.smsFormSentAgo')} <LiveAgo from={lead.awaiting_payment_sms_sent_at} />
            </div>
          ) : null}
          {lead.report_sms_sent_at ? (
            <div className="mb-2 text-xs text-[color:var(--stryzh-color-text-secondary)]">
              {t('leads.detail.smsReportSentAgo')} <LiveAgo from={lead.report_sms_sent_at} />
            </div>
          ) : null}
          <div className="flex flex-col gap-2 [&_button]:w-full">
            <Button
              type="button"
              variant="secondary"
              disabled={!formattedPhone || notInWork}
              onClick={() => {
                if (lead.client_phone) window.location.href = `tel:${lead.client_phone}`
              }}
            >
              {formattedPhone ? t('leads.detail.callWith', { phone: formattedPhone }) : t('leads.detail.callNoPhone')}
            </Button>
            {ui.callResults ? <LeadCallResultControl lead={lead} flushDraft={flushDraft} /> : null}
            {ui.clientSms ? (
              <>
                <SendSmsToLeadButton
                  leadId={lead.id}
                  clientPhone={lead.client_phone}
                  landingToken={lead.landing_token ?? ''}
                  beforeOpen={flushDraft}
                  disabled={notInWork}
                />
                <SendStatusSmsButton
                  leadId={lead.id}
                  clientPhone={lead.client_phone}
                  landingToken={lead.landing_token ?? ''}
                  beforeOpen={flushDraft}
                  disabled={notInWork}
                />
                <SendReportSmsButton
                  leadId={lead.id}
                  clientPhone={lead.client_phone}
                  landingToken={lead.landing_token ?? ''}
                  reportReady={!!lead.order_report_url}
                  beforeOpen={flushDraft}
                  disabled={notInWork}
                />
              </>
            ) : null}
          </div>
        </div>

        {/* --- Этап (ниже связи с клиентом: основная работа ФО: звонок и его результат) --- */}
        <div>
          <div className={groupHead}>{t('leads.detail.palStage')}</div>
          {stage}
        </div>

        {/* --- Глазами клиента --- */}
        {hasToken ? (
          <div>
            <div className={groupHead}>{t('leads.detail.palAsClient')}</div>
            <div className="flex flex-col gap-2 [&_button]:w-full">
              <Button
                type="button"
                variant="secondary"
                disabled={notInWork}
                onClick={async () => {
                  await flushDraft()
                  window.open(formUrl!, '_blank', 'noopener')
                }}
              >
                {t('leads.detail.openForm')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={notInWork}
                onClick={() => {
                  void navigator.clipboard?.writeText(formUrlClean!)
                }}
              >
                {t('leads.detail.copy')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={notInWork}
                onClick={async () => {
                  await flushDraft()
                  window.open(statusUrl!, '_blank', 'noopener')
                }}
              >
                {t('leads.detail.openStatus')}
              </Button>
              <Button
                type="button"
                variant="ghost"
                disabled={notInWork}
                onClick={() => {
                  void navigator.clipboard?.writeText(statusUrlClean!)
                }}
              >
                {t('leads.detail.copy')}
              </Button>
            </div>
          </div>
        ) : null}

        {/* --- Автотека --- */}
        {autoteka ? (
          <div className="[&_button]:w-full">
            <div className={groupHead}>{t('leads.detail.palAutoteka')}</div>
            {autoteka}
          </div>
        ) : null}

        {/* --- Заказ --- */}
        {order ? (
          <div className="[&_button]:w-full">
            <div className={groupHead}>{t('leads.detail.palOrder')}</div>
            {order}
          </div>
        ) : null}

        {/* --- Управление --- */}
        {lead.status !== 'cancelled' || manageExtra ? (
          <div className="flex flex-col gap-3">
            <div className={groupHead}>{t('leads.detail.palManage')}</div>
            {lead.status !== 'cancelled' ? (
              <Button
                type="button"
                variant="danger"
                className="w-full"
                disabled={cancelPending}
                onClick={onCancel}
              >
                {cancelPending ? t('leads.detail.cancelling') : t('leads.detail.cancelLead')}
              </Button>
            ) : null}
            {manageExtra}
          </div>
        ) : null}

        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200">
            {error}
          </div>
        ) : null}
      </div>
    </section>
  )
}
